const SELECT_COMPONENT_KEYS = ['xmDataSelect', 'Select', 'EnumRel', 'xmSelect', 'xmUserSelect', 'UserSelect'];

/** 按长度降序，避免 notIn 被误判为 in */
const QUERY_OPERATOR_SUFFIXES = [
  'multiLike', 'notLike', 'startWith', 'endWith',
  'isNotEmpty', 'isEmpty', 'notNull', 'isNull',
  'notIn', 'between', 'like', 'eq', 'neq', 'gte', 'lte',
  'in', 'ge', 'le', 'gt', 'lt', 'nq',
];

/** 字段 key 是否已带查询 operator 后缀（如 field_notIn、field_in） */
export function hasQueryOperatorSuffix(fieldKey: string): boolean {
  if (!fieldKey) return false;
  return QUERY_OPERATOR_SUFFIXES.some((operator) => {
    const suffixToken = `_${operator}`;
    return fieldKey.endsWith(suffixToken) && fieldKey.length > suffixToken.length;
  });
}

/**
 * 根据查询表单子组件配置，将用户输入的字段名解析为实际 model key
 * 支持不带 _in 等后缀的字段名自动匹配
 */
export function isQueryFormFieldParam(fieldId: string, children?: any[]): boolean {
  if (!fieldId || !children?.length) {
    return false;
  }
  const resolvedKey = resolveQueryFormFieldKey(fieldId, children);
  return children.some((child) => child.model?.key === resolvedKey || resolveChildStoreKey(child) === resolvedKey);
}

/**
 * 从路由参数中筛选出可写入 xmQueryForm 查询项的参数
 */
export function pickQueryFormRouteParams(
  routeParams: Record<string, any>,
  children?: any[],
): Record<string, any> {
  if (!routeParams || !children?.length) {
    return {};
  }
  const picked: Record<string, any> = {};
  Object.keys(routeParams).forEach((key) => {
    if (key === 'moreQueryConfigs') {
      picked[key] = routeParams[key];
      return;
    }
    if (isQueryFormFieldParam(key, children)) {
      picked[key] = routeParams[key];
    }
  });
  return picked;
}

export function resolveQueryFormFieldKey(fieldId: string, children?: any[]): string {
  if (!fieldId) {
    return fieldId;
  }
  if (!children?.length) {
    return resolveQueryFormFieldKeyFromState(fieldId, {});
  }

  const exactChild = children.find((child) => child.model?.key === fieldId);
  if (exactChild) {
    return resolveChildStoreKey(exactChild);
  }

  const suffixChild = children.find((child) => {
    const key = child.model?.key || '';
    return key.startsWith(`${fieldId}_`);
  });
  if (suffixChild) {
    return resolveChildStoreKey(suffixChild);
  }

  const baseChild = children.find((child) => {
    const key = child.model?.key || '';
    const baseName = stripQueryOperatorSuffix(key);
    return baseName === fieldId;
  });
  if (baseChild) {
    return resolveChildStoreKey(baseChild);
  }

  return fieldId;
}

/**
 * 根据已有表单状态 key 推断实际字段名（组件未挂载时的兜底）
 */
export function resolveQueryFormFieldKeyFromState(fieldId: string, formState: Record<string, any>): string {
  if (!fieldId) {
    return fieldId;
  }
  if (formState && fieldId in formState) {
    return fieldId;
  }
  const stateKeys = Object.keys(formState || {});
  const exactSuffixKey = stateKeys.find((key) => key === `${fieldId}_in`);
  if (exactSuffixKey) {
    return exactSuffixKey;
  }
  const prefixKey = stateKeys.find((key) => key.startsWith(`${fieldId}_`));
  if (prefixKey) {
    return prefixKey;
  }
  return fieldId;
}

function stripQueryOperatorSuffix(fieldKey: string): string {
  for (const operator of QUERY_OPERATOR_SUFFIXES) {
    const suffixToken = `_${operator}`;
    if (fieldKey.endsWith(suffixToken) && fieldKey.length > suffixToken.length) {
      return fieldKey.slice(0, -suffixToken.length);
    }
  }
  return fieldKey.split('_')[0];
}

function resolveChildStoreKey(child: any): string {
  const key = child.model?.key || '';
  const componentKey = child.componentKey || '';
  if (SELECT_COMPONENT_KEYS.includes(componentKey)) {
    if (hasQueryOperatorSuffix(key)) {
      return key;
    }
    const multiple = child.props?.multiple === true;
    if (multiple) {
      return `${key}_in`;
    }
    return key;
  }
  return key;
}
